

import React, { useState } from 'react';
import { Avatar, Button, Typography, Box, CircularProgress } from '@mui/material';
import { PhotoCamera, Edit as EditIcon } from '@mui/icons-material';
import { useFileUpload } from '../../hooks/useFileUpload';
import useGlobal from '../../util/Global';

const photoStyles = {
    container: { textAlign: 'center', pt: 5 },
    title: { marginBottom: 16, color: 'text.secondary' },
    avatarBox: (preview) => ({
        width: 150,
        height: 150,
        margin: 'auto',
        borderRadius: '50%',
        border: '2px dashed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        backgroundColor: preview ? 'transparent' : '#f0f0f0',
        mb: 2,
    }),
    button: { borderRadius: 8, textTransform: 'none' },
    error: { mt: 1, color: 'error.main' },
};

/**
 * @param {{ initialPreview?: string, onChange?: (file: File) => Promise<any> }} props
 */
export const PhotoUploader = ({ initialPreview = null, isLoading = false, onChange }) => {

    const { t } = useGlobal();
    const { preview, error, inputRef, handleFileChange: internalHandleFileChange } = useFileUpload({
        initialPreview,
        validator: (file) => {
            const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
            const ext = file.name.split('.').pop().toLowerCase();
            if (!validTypes.includes(file.type) && !['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
                return t('message.SelectImageType');
            }
            if (file.size > 5 * 1024 * 1024) {
                return t('message.SelectImageType5MB')
            }
            return '';
        },
        onPreview: (file, setPreview) => {
            const reader = new FileReader();
            reader.onload = () => setPreview(reader.result);
            reader.readAsDataURL(file);
        },
        onFileChange: async (file) => {
            if (onChange) {
                try {
                    //     setIsLoading(true);
                    await onChange(file);
                } finally {
                    //   setIsLoading(false);
                }
            }
        },
    });

    const handleFileChange = (e) => {
        // Prevent new selection while loading
        if (isLoading) return;
        internalHandleFileChange(e);
    };

    const getLabel = () => {
        if (isLoading)
            return t('button.uploading');

        if (preview != undefined)
            return t('col.changePhoto')

        return t('col.addPhoto')
    }

    return (
        <Box sx={photoStyles.container}>
            <Box sx={photoStyles.avatarBox(Boolean(preview))}>
                <Avatar src={preview} sx={{ width: '100%', height: '100%' }}>
                    {!preview && <PhotoCamera fontSize="large" />}
                </Avatar>
            </Box>
            <input
                type="file"
                accept=".jpg,.jpeg,.png,.gif"
                ref={inputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={isLoading}
            />
            <Button
                variant="outlined"
                onClick={() => inputRef.current?.click()}
                sx={photoStyles.button}
                startIcon={isLoading ? <CircularProgress size={24} /> : <EditIcon />}
                disabled={isLoading}
            >
                {getLabel()}
            </Button>
            {error && <Typography variant="body2" sx={photoStyles.error}>{error}</Typography>}
        </Box>
    );
}